/**
 * Reports Page
 * Weekly reports, export functionality
 */

import React, { useEffect, useState } from 'react';
import '../styles/validation.css';

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

interface WeeklyReport {
  week_number: number;
  start_date: string;
  end_date: string;
  total_trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  total_pnl: number;
  weekly_return: number;
  max_drawdown: number;
  sharpe_ratio: number;
  avg_trade_duration: number;
  best_trade: number;
  worst_trade: number;
  enhancement_performance: {
    [key: string]: {
      contribution: number;
      status: string;
    };
  };
}

const Reports: React.FC = () => {
  const [weeks, setWeeks] = useState<WeeklyReport[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await fetch(`${API_BASE}/weekly-report`);
        const data = await res.json();
        setWeeks(data.data || []);
        if (data.data && data.data.length > 0) {
          setSelectedWeek(data.data[0]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reports:', error);
        setLoading(false);
      }
    };

    fetchReports();
    const interval = setInterval(fetchReports, 600000); // 10 min
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="validation-container"><div className="loading">Loading reports...</div></div>;

  const handleExportCSV = (week: WeeklyReport) => {
    const csv = [
      ['Weekly Report - Week ' + week.week_number],
      ['Period', `${week.start_date} to ${week.end_date}`],
      [],
      ['Performance Metrics'],
      ['Total Trades', week.total_trades],
      ['Wins', week.wins],
      ['Losses', week.losses],
      ['Win Rate', (week.win_rate * 100).toFixed(2) + '%'],
      ['Total P&L', '$' + week.total_pnl.toFixed(2)],
      ['Weekly Return', (week.weekly_return * 100).toFixed(2) + '%'],
      ['Max Drawdown', (week.max_drawdown * 100).toFixed(2) + '%'],
      ['Sharpe Ratio', week.sharpe_ratio.toFixed(2)],
      ['Avg Trade Duration', week.avg_trade_duration + ' min'],
      ['Best Trade', '$' + week.best_trade.toFixed(2)],
      ['Worst Trade', '$' + week.worst_trade.toFixed(2)],
      [],
      ['Enhancement Performance'],
      ...Object.entries(week.enhancement_performance).map(([name, data]: any) => [
        name,
        'Contribution: ' + (data.contribution * 100).toFixed(2) + '%',
        'Status: ' + data.status
      ])
    ];

    const csvContent = csv.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trading_report_week_${week.week_number}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSubscribe = () => {
    if (email) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Weekly Reports</h1>
        <p className="subtitle">Performance summaries, enhancement analysis, export & subscribe</p>
      </div>

      {/* Email Subscription */}
      <div className="widget subscription-widget">
        <h3>Email Subscription</h3>
        <p>Get weekly reports delivered to your inbox every Monday</p>
        <div className="subscription-form">
          <input
            type="email"
            placeholder="your.email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="email-input"
          />
          <button className="subscribe-btn" onClick={handleSubscribe}>
            {subscribed ? '✓ Subscribed' : 'Subscribe to Reports'}
          </button>
        </div>
        {subscribed && <p className="success-message">You'll receive reports every Monday at 9 AM UTC</p>}
      </div>

      {/* Week Selector */}
      <div className="widget">
        <h3>Select Week</h3>
        <div className="week-selector">
          {weeks.map((week) => (
            <button
              key={week.week_number}
              className={`week-btn ${selectedWeek?.week_number === week.week_number ? 'active' : ''}`}
              onClick={() => setSelectedWeek(week)}
            >
              Week {week.week_number}
            </button>
          ))}
        </div>
      </div>

      {/* Selected Week Report */}
      {selectedWeek && (
        <>
          {/* Report Header */}
          <div className="report-header-section">
            <div className="report-title">
              <h2>Week {selectedWeek.week_number} Report</h2>
              <p className="report-dates">{selectedWeek.start_date} to {selectedWeek.end_date}</p>
            </div>
            <div className="report-export">
              <button
                className="export-btn"
                onClick={() => handleExportCSV(selectedWeek)}
              >
                📥 Export to CSV
              </button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="report-metrics-grid">
            <div className="report-metric">
              <div className="metric-label">Total Trades</div>
              <div className="metric-value">{selectedWeek.total_trades}</div>
              <div className="metric-breakdown">
                {selectedWeek.wins} W / {selectedWeek.losses} L
              </div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Win Rate</div>
              <div className="metric-value">{(selectedWeek.win_rate * 100).toFixed(0)}%</div>
              <div className="metric-breakdown">Closing trades profitably</div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Weekly Return</div>
              <div className="metric-value" style={{ color: selectedWeek.weekly_return > 0 ? '#4CAF50' : '#F44336' }}>
                {(selectedWeek.weekly_return * 100).toFixed(2)}%
              </div>
              <div className="metric-breakdown">Total P&L ${selectedWeek.total_pnl.toFixed(2)}</div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Sharpe Ratio</div>
              <div className="metric-value">{selectedWeek.sharpe_ratio.toFixed(2)}</div>
              <div className="metric-breakdown">Risk-adjusted returns</div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Max Drawdown</div>
              <div className="metric-value">{(selectedWeek.max_drawdown * 100).toFixed(2)}%</div>
              <div className="metric-breakdown">Peak-to-trough decline</div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Avg Trade Duration</div>
              <div className="metric-value">{selectedWeek.avg_trade_duration}m</div>
              <div className="metric-breakdown">Minutes in average trade</div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Best Trade</div>
              <div className="metric-value" style={{ color: '#4CAF50' }}>
                ${selectedWeek.best_trade.toFixed(2)}
              </div>
              <div className="metric-breakdown">Largest winning trade</div>
            </div>
            <div className="report-metric">
              <div className="metric-label">Worst Trade</div>
              <div className="metric-value" style={{ color: '#F44336' }}>
                ${selectedWeek.worst_trade.toFixed(2)}
              </div>
              <div className="metric-breakdown">Largest losing trade</div>
            </div>
          </div>

          {/* Enhancement Performance */}
          {selectedWeek.enhancement_performance && (
            <div className="widget">
              <h3>Enhancement Performance</h3>
              <div className="enhancements-performance-grid">
                {Object.entries(selectedWeek.enhancement_performance).map(([name, data]: any) => (
                  <div key={name} className={`enhancement-performance-card ${data.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                    <div className="enhancement-name">{name.replace(/_/g, ' ').toUpperCase()}</div>
                    <div className="enhancement-contribution">
                      <span className="label">Contribution:</span>
                      <span className="value">{(data.contribution * 100).toFixed(1)}%</span>
                    </div>
                    <div className="enhancement-status">
                      <span className="label">Status:</span>
                      <span className={`badge ${data.status.toLowerCase()}`}>{data.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trade Performance Summary */}
          <div className="widget summary-widget">
            <h3>Trade Performance Summary</h3>
            <div className="summary-sections">
              <div className="summary-section">
                <h4>Winning Trades</h4>
                <div className="summary-item">
                  <span className="label">Count:</span>
                  <span className="value">{selectedWeek.wins}</span>
                </div>
              </div>
              <div className="summary-section">
                <h4>Losing Trades</h4>
                <div className="summary-item">
                  <span className="label">Count:</span>
                  <span className="value">{selectedWeek.losses}</span>
                </div>
              </div>
              <div className="summary-section">
                <h4>Profitability</h4>
                <div className="summary-item">
                  <span className="label">Profit Factor:</span>
                  <span className="value">{(selectedWeek.total_pnl / Math.abs(selectedWeek.worst_trade * selectedWeek.losses) || 1).toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="summary-recommendation">
              <p>
                <strong>Recommendation:</strong>{' '}
                {selectedWeek.win_rate > 0.65
                  ? 'System performing well. Continue current strategy.'
                  : selectedWeek.win_rate > 0.55
                  ? 'System performing adequately. Monitor for improvements.'
                  : 'Review enhancement settings and market conditions.'}
              </p>
            </div>
          </div>

          {/* Validation Status */}
          <div className="widget validation-status-widget">
            <h3>40-Day Validation Progress</h3>
            <div className="validation-progress">
              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${(selectedWeek.week_number / 6) * 100}%` }}>
                  <span className="progress-text">{selectedWeek.week_number} of 6 weeks</span>
                </div>
              </div>
              <div className="validation-milestones">
                <div className={`milestone ${selectedWeek.week_number >= 1 ? 'complete' : ''}`}>
                  <span className="milestone-marker">●</span>
                  <span className="milestone-label">Week 1: Baseline</span>
                </div>
                <div className={`milestone ${selectedWeek.week_number >= 2 ? 'complete' : ''}`}>
                  <span className="milestone-marker">●</span>
                  <span className="milestone-label">Week 2: Optimization</span>
                </div>
                <div className={`milestone ${selectedWeek.week_number >= 3 ? 'complete' : ''}`}>
                  <span className="milestone-marker">●</span>
                  <span className="milestone-label">Week 3: Stabilization</span>
                </div>
                <div className={`milestone ${selectedWeek.week_number >= 4 ? 'complete' : ''}`}>
                  <span className="milestone-marker">●</span>
                  <span className="milestone-label">Week 4-6: Confirmation</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
